/**
 * La scala del compenso: i gradini del profilo lungo i mesi di abbonamento (geometria in `@shared/partner/scala`),
 * col segno del mese di oggi quando si guarda un'azienda. È l'unico disegno della pagina: il resto resta tabella.
 *
 * L'ultimo gradino «per sempre» sfuma verso destra: non finisce, continua oltre il bordo.
 */

import React from 'react';
import { Box, Tooltip, Typography, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import type { ScaglioneCompenso } from '@shared/partner/compensoPartner';
import { disegnaScala } from '@shared/partner/scala';
import { useFormati } from './formati';

interface Props {
  scaglioni: readonly ScaglioneCompenso[];
  /** Il mese di abbonamento di oggi dell'azienda: disegna il segno. */
  meseDiOggi?: number | null;
  /** Per le righe delle tabelle: più bassa, senza i numeri dei mesi. */
  compatta?: boolean;
}

export const ScalaCompenso: React.FC<Props> = ({ scaglioni, meseDiOggi, compatta = false }) => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const scala = disegnaScala(scaglioni, meseDiOggi);
  if (scala.gradini.length === 0) return null;

  const colore = tokens.color.tierBusiness.main;
  const altezza = compatta ? 22 : 40;
  const spazioEtichette = 16;

  const descrizione = scala.gradini
    .map((g) => (g.al_mese === null
      ? t('profili.scaglione_per_sempre', { dal: g.dal_mese, percentuale: f.percentuale(g.percentuale) })
      : t('profili.scaglione', { dal: g.dal_mese, al: g.al_mese, percentuale: f.percentuale(g.percentuale) })))
    .join('; ');

  return (
    <Box role="img" aria-label={t('scala.etichetta', { descrizione })} sx={{ width: '100%', minWidth: compatta ? 140 : 220 }}>
      <Box sx={{ position: 'relative', height: altezza + spazioEtichette }}>
        {scala.gradini.map((g) => {
          const alta = Math.max(4, g.altezza * altezza);
          const perSempre = g.al_mese === null;
          return (
            <Box key={g.dal_mese} sx={{ position: 'absolute', left: `${g.inizio * 100}%`, width: `${g.larghezza * 100}%`, bottom: 0, height: alta + spazioEtichette }}>
              <Typography
                sx={{
                  position: 'absolute', top: 0, left: 4, fontSize: compatta ? '0.68rem' : '0.75rem', fontWeight: 700,
                  color: colore, lineHeight: 1, whiteSpace: 'nowrap',
                }}
              >
                {f.percentuale(g.percentuale)}
              </Typography>
              <Box
                sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: alta,
                  borderLeft: g.dal_mese > 1 ? `1px solid ${alpha(colore, 0.35)}` : 'none',
                  background: perSempre
                    ? `linear-gradient(to right, ${alpha(colore, 0.16)} 55%, ${alpha(colore, 0)} 100%)`
                    : alpha(colore, 0.16),
                }}
              >
                {/* Il bordo del gradino: pieno, o sfumato come il gradino se continua oltre. */}
                <Box
                  sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                    background: perSempre ? `linear-gradient(to right, ${colore} 55%, ${alpha(colore, 0)} 100%)` : colore,
                  }}
                />
              </Box>
            </Box>
          );
        })}
        {scala.oggi && (
          <Tooltip title={t('scala.oggi', { mese: scala.oggi.mese, percentuale: f.percentuale(scala.oggi.percentuale) })}>
            <Box sx={{ position: 'absolute', left: `${scala.oggi.posizione * 100}%`, top: 0, bottom: 0, width: 0, cursor: 'help' }}>
              <Box sx={{ position: 'absolute', top: 2, bottom: 0, left: -1, width: 2, bgcolor: tokens.color.graphite }} />
              <Box sx={{ position: 'absolute', bottom: -4, left: -5, width: 10, height: 10, borderRadius: '50%', bgcolor: tokens.color.graphite, border: `2px solid ${tokens.color.paper}` }} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {!compatta && (
        <Box sx={{ position: 'relative', height: 16, mt: 0.5 }}>
          {scala.gradini.map((g) => (
            <Typography key={g.dal_mese} sx={{ position: 'absolute', left: `${g.inizio * 100}%`, fontSize: '0.68rem', color: tokens.color.mist, pl: 0.5 }}>
              {t('scala.mese', { mese: g.dal_mese })}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
